// src/components/DataStatusIndicator.tsx
import React from 'react';

export type DataStatus = 'live' | 'cached' | 'error';

interface DataStatusIndicatorProps {
  status: DataStatus;
  lastUpdated?: number;
}

const DataStatusIndicator: React.FC<DataStatusIndicatorProps> = ({ status, lastUpdated }) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'live':
        return {
          color: 'bg-green-500',
          pulseColor: 'bg-green-400',
          text: 'Live',
          textColor: 'text-green-600 dark:text-green-400'
        };
      case 'cached':
        return {
          color: 'bg-yellow-500',
          pulseColor: 'bg-yellow-400',
          text: 'Cached',
          textColor: 'text-yellow-600 dark:text-yellow-400'
        };
      case 'error':
        return {
          color: 'bg-red-500',
          pulseColor: 'bg-red-400',
          text: 'Error',
          textColor: 'text-red-600 dark:text-red-400'
        };
    }
  };

  const config = getStatusConfig();
  
  const getTimeSince = () => {
    if (!lastUpdated) return '';
    const seconds = Math.floor((Date.now() - lastUpdated) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const timeSince = getTimeSince();

  return (
    <div className="absolute top-2 right-2 flex items-center gap-2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full px-3 py-1 shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="relative flex items-center">
        <span className={`relative flex h-3 w-3`}>
          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${config.pulseColor} opacity-75`}></span>
          <span className={`relative inline-flex rounded-full h-3 w-3 ${config.color}`}></span>
        </span>
      </div>
      <span className={`text-xs font-medium ${config.textColor}`}>
        {config.text}
        {timeSince && <span className="text-gray-500 dark:text-gray-400 ml-1">({timeSince})</span>}
      </span>
    </div>
  );
};

export default DataStatusIndicator;